/**
 * Deterministic "related games" and "related states" link sets.
 *
 * Every state hub and game page gets a stable, page-specific set of internal
 * links so crawlers always find several new paths from any entry point while
 * the markup stays identical between builds (no random churn in the HTML).
 */

import { STATES } from "@/data/states";
import { ALL_GAMES, type Game } from "@/data/games";
import { hashSeed } from "@/lib/page-copy";

export interface RelatedLink {
  label: string;
  href: string;
  description?: string;
}

const LOTTERY_STATES = STATES.filter((s) => s.has_lottery);

/** Stable slice of a list, starting at a hash-derived offset. */
function stableSample<T>(seed: string, items: T[], count: number): T[] {
  if (items.length === 0) return [];
  const start = hashSeed(seed) % items.length;
  const out: T[] = [];
  for (let i = 0; i < Math.min(count, items.length); i++) {
    out.push(items[(start + i * 3) % items.length]);
  }
  // De-duplicate in case the stride wraps onto an earlier element.
  return Array.from(new Set(out));
}

/** URL slug for a game, relative to the state it is displayed under. */
export function gameHref(game: Pick<Game, "slug" | "is_multistate">, stateSlug?: string): string {
  if (game.is_multistate) return `/games/${game.slug}`;
  const slug =
    stateSlug && game.slug.startsWith(`${stateSlug}-`)
      ? game.slug.slice(stateSlug.length + 1)
      : game.slug;
  const owner = stateSlug ?? stateSlugForGame(game.slug);
  return owner ? `/${owner}/${slug}` : `/games/${game.slug}`;
}

/** Infer the owning state slug from a prefixed state-game slug. */
export function stateSlugForGame(gameSlug: string): string | null {
  const match = LOTTERY_STATES.filter((s) => gameSlug.startsWith(`${s.slug}-`)).sort(
    (a, b) => b.slug.length - a.slug.length,
  )[0];
  return match?.slug ?? null;
}

const MULTISTATE = ALL_GAMES.filter((g) => g.is_multistate);

/** Other lottery states, deterministically chosen for the given page. */
export function relatedStates(seedSlug: string, excludeSlug?: string, count = 6): RelatedLink[] {
  const pool = LOTTERY_STATES.filter((s) => s.slug !== excludeSlug);
  return stableSample(`${seedSlug}-states`, pool, count).map((s) => ({
    label: `${s.name} lottery results`,
    href: `/states/${s.slug}`,
    description: `Winning numbers and jackpots for every ${s.abbreviation} draw game.`,
  }));
}

/** Games worth linking from a state hub: the national games plus draws elsewhere. */
export function relatedGamesForState(stateSlug: string, count = 6): RelatedLink[] {
  const inState = ALL_GAMES.filter((g) => !g.is_multistate && g.slug.startsWith(`${stateSlug}-`));
  const elsewhere = ALL_GAMES.filter(
    (g) => !g.is_multistate && !g.slug.startsWith(`${stateSlug}-`),
  );
  const picks: Game[] = [
    ...MULTISTATE.slice(0, 2),
    ...stableSample(`${stateSlug}-ingame`, inState, 2),
    ...stableSample(`${stateSlug}-outgame`, elsewhere, count),
  ];
  const seen = new Set<string>();
  const out: RelatedLink[] = [];
  for (const g of picks) {
    if (seen.has(g.slug)) continue;
    seen.add(g.slug);
    const owner = g.is_multistate ? undefined : stateSlugForGame(g.slug) ?? undefined;
    const ownerState = owner ? STATES.find((s) => s.slug === owner) : undefined;
    out.push({
      label: ownerState ? `${ownerState.name} ${g.name}` : g.name,
      href: gameHref(g, owner),
      description: g.draw_schedule ? `Drawn ${g.draw_schedule}.` : undefined,
    });
    if (out.length >= count) break;
  }
  return out;
}

/** Sibling games to link from a game page. */
export function relatedGamesForGame(gameSlug: string, stateSlug?: string, count = 6): RelatedLink[] {
  const owner = stateSlug ?? stateSlugForGame(gameSlug) ?? undefined;
  const siblings = owner
    ? ALL_GAMES.filter((g) => !g.is_multistate && g.slug.startsWith(`${owner}-`) && g.slug !== gameSlug)
    : [];
  const others = ALL_GAMES.filter((g) => g.slug !== gameSlug && !siblings.includes(g));
  const picks: Game[] = [
    ...MULTISTATE.filter((g) => g.slug !== gameSlug).slice(0, 2),
    ...stableSample(`${gameSlug}-sib`, siblings, 3),
    ...stableSample(`${gameSlug}-other`, others, count),
  ];
  const seen = new Set<string>();
  const out: RelatedLink[] = [];
  for (const g of picks) {
    if (seen.has(g.slug)) continue;
    seen.add(g.slug);
    const gOwner = g.is_multistate ? undefined : stateSlugForGame(g.slug) ?? undefined;
    const gState = gOwner ? STATES.find((s) => s.slug === gOwner) : undefined;
    out.push({
      label: gState ? `${gState.name} ${g.name}` : g.name,
      href: gameHref(g, gOwner),
      description: g.draw_schedule ? `Drawn ${g.draw_schedule}.` : undefined,
    });
    if (out.length >= count) break;
  }
  return out;
}

/** States to link from a game page (states that sell it, or nearby markets). */
export function relatedStatesForGame(
  gameSlug: string,
  isMultistate: boolean,
  stateSlug?: string,
  count = 6,
): RelatedLink[] {
  const links = relatedStates(`${gameSlug}-${stateSlug ?? "us"}`, stateSlug, count);
  if (!isMultistate) return links;
  return links.map((l) => ({
    ...l,
    description: `See ${l.label.replace(" lottery results", "")} jackpots and every game sold there.`,
  }));
}
